import { CUSTOMER_PERMISSIONS, EMPLOYEE_PERMISSIONS } from '@/types/permissions';

import type { RouteDefinition } from './route-config';

function defineRoute<TParams = never>(config: RouteDefinition<TParams>): RouteDefinition<TParams> {
  return config;
}

const menuGroups = {
  main: {
    employees: 'menu.employees',
    integrations: 'menu.integrations',
    reports: 'menu.reports',
    invoices: 'menu.invoices_group',
  },
};

export const routeDefinitions = {
  Admin: {
    Login: defineRoute({
      name: 'admin.login',
      path: '/admin/login',
      component: () => import('../views/Admin/Login.vue'),
      meta: { requiresGuest: true, context: 'employee' },
    }),

    Dashboard: defineRoute({
      name: 'admin.dashboard',
      path: '/admin/dashboard',
      component: () => import('../views/Admin/Dashboard.vue'),
      meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
      menus: {
        main: { label: 'menu.dashboard', icon: 'pi pi-home' },
      },
    }),

    Organizations: {
      Index: defineRoute({
        name: 'admin.organizations.index',
        path: '/admin/organizations',
        component: () => import('../views/Admin/Organizations.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.ORGANIZATIONS_SHOW,
        },
        menus: {
          main: { label: 'menu.organizations', icon: 'pi pi-sitemap' },
        },
      }),
    },

    Companies: {
      Index: defineRoute({
        name: 'admin.companies.index',
        path: '/admin/companies',
        component: () => import('../views/Admin/Companies.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
        },
        menus: {
          main: { label: 'menu.companies', icon: 'pi pi-building' },
        },
      }),
      Create: defineRoute({
        name: 'admin.companies.create',
        path: '/admin/companies/create',
        component: () => import('../views/Admin/Companies/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.COMPANIES_CREATE,
          context: 'employee',
        },
      }),
      Show: defineRoute({
        name: 'admin.companies.show',
        path: '/admin/companies/:id',
        component: () => import('../views/Admin/Companies/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
        },
        params: {} as { id: string },
        redirect: to => ({ name: 'admin.companies.details', params: to.params as Record<string, unknown> }),
        children: {
          Details: defineRoute({
            name: 'admin.companies.details',
            path: '',
            component: () => import('../views/Admin/Companies/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
            },
            params: {} as { id: string },
          }),
          Members: defineRoute({
            name: 'admin.companies.members',
            path: 'members',
            component: () => import('../views/Admin/Companies/MembersTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
            },
            params: {} as { id: string },
          }),
          Brands: defineRoute({
            name: 'admin.companies.brands',
            path: 'brands',
            component: () => import('../views/Admin/Companies/BrandsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
            },
            params: {} as { id: string },
          }),
          Roles: defineRoute({
            name: 'admin.companies.roles',
            path: 'roles',
            component: () => import('../views/Admin/Companies/RolesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
            },
            params: {} as { id: string },
          }),
          Activity: defineRoute({
            name: 'admin.companies.activity',
            path: 'activity',
            component: () => import('../views/Admin/Companies/ActivityLogTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.COMPANIES_SHOW,
            },
            params: {} as { id: string },
          }),
        },
      }),
      Edit: defineRoute({
        name: 'admin.companies.edit',
        path: '/admin/companies/:id/edit',
        component: () => import('../views/Admin/Companies/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.COMPANIES_CREATE,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
    },

    Brands: {
      Index: defineRoute({
        name: 'admin.brands.index',
        path: '/admin/brands',
        component: () => import('../views/Admin/Brands.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.brands', icon: 'pi pi-tag' },
        },
      }),
      Create: defineRoute({
        name: 'admin.brands.create',
        path: '/admin/brands/create',
        component: () => import('../views/Admin/Brands/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.BRANDS_CREATE,
          context: 'employee',
        },
      }),
      Show: defineRoute({
        name: 'admin.brands.show',
        path: '/admin/brands/:id',
        component: () => import('../views/Admin/Brands/Show.vue'),
        redirect: to => ({ name: 'admin.brands.details', params: to.params as Record<string, unknown> }),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
          context: 'employee',
        },
        params: {} as { id: string },
        children: {
          Details: defineRoute({
            name: 'admin.brands.details',
            path: '',
            component: () => import('../views/Admin/Brands/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
            },
            params: {} as { id: string },
          }),
          Markets: defineRoute({
            name: 'admin.brands.markets',
            path: 'markets',
            component: () => import('../views/Admin/Brands/MarketsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_MARKETS_SHOW,
            },
            params: {} as { id: string },
          }),
          Credentials: defineRoute({
            name: 'admin.brands.credentials',
            path: 'credentials',
            component: () => import('../views/Admin/Brands/CredentialsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
            },
            params: {} as { id: string },
          }),
          CallbackUrls: defineRoute({
            name: 'admin.brands.callback-urls',
            path: 'callback-urls',
            component: () => import('../views/Admin/Brands/CallbackUrlsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_CALLBACK_URL_SHOW,
            },
            params: {} as { id: string },
          }),
          Providers: defineRoute({
            name: 'admin.brands.providers',
            path: 'providers',
            component: () => import('../views/Admin/Brands/ProvidersTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
            },
            params: {} as { id: string },
          }),
          Games: defineRoute({
            name: 'admin.brands.games',
            path: 'games',
            component: () => import('../views/Admin/Brands/GamesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
            },
            params: {} as { id: string },
          }),
          CallbackTests: defineRoute({
            name: 'admin.brands.callback-tests',
            path: 'callback-tests',
            component: () => import('../views/Admin/Brands/CallbackTests.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
            },
            params: {} as { id: string },
          }),
          RequestAudit: defineRoute({
            name: 'admin.brands.request-audit',
            path: 'request-audit',
            component: () => import('../views/Admin/Brands/CallbackAuditTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_CALLBACK_URL_SHOW,
            },
            params: {} as { id: string },
          }),
          PublicApi: defineRoute({
            name: 'admin.brands.public-api',
            path: 'public-api',
            component: () => import('../views/Admin/Brands/PublicApiTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_PUBLIC_API_TESTING_USE,
            },
            params: {} as { id: string },
          }),
          ActivityLog: defineRoute({
            name: 'admin.brands.activity-log',
            path: 'activity-log',
            component: () => import('../views/Admin/Brands/ActivityLogTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_ACTIVITY_LOG_VIEW,
            },
            params: {} as { id: string },
          }),
        },
      }),
      Edit: defineRoute({
        name: 'admin.brands.edit',
        path: '/admin/brands/:id/edit',
        component: () => import('../views/Admin/Brands/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.BRANDS_UPDATE,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
    },

    Transactions: {
      Dashboard: defineRoute({
        name: 'admin.transactions.dashboard',
        path: '/admin/transactions',
        component: () => import('../views/Admin/Transactions/Dashboard.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.TRANSACTIONS_SHOW,
        },
        menus: {
          main: { label: 'menu.transactions', icon: 'pi pi-chart-bar' },
        },
      }),
      Live: defineRoute({
        name: 'admin.transactions.live',
        path: '/admin/transactions/live',
        component: () => import('../views/Admin/Transactions/Live.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.TRANSACTIONS_SHOW,
        },
      }),
      Details: defineRoute({
        name: 'admin.transactions.details',
        path: '/admin/transactions/:transactionId',
        component: () => import('../views/Admin/Transactions/Details.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.TRANSACTIONS_SHOW,
        },
        params: {} as { transactionId: string },
      }),
    },

    GameSessions: {
      Show: defineRoute({
        name: 'admin.game_sessions.show',
        path: '/admin/game-sessions/:id',
        component: () => import('../views/Admin/GameSessions/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.PLAYERS_SHOW,
        },
        params: {} as { id: string },
      }),
    },

    Players: {
      Index: defineRoute({
        name: 'admin.players.index',
        path: '/admin/players',
        component: () => import('../views/Admin/Players.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.PLAYERS_SHOW,
        },
        menus: {
          main: { label: 'menu.players', icon: 'pi pi-users' },
        },
      }),
      Show: defineRoute({
        name: 'admin.players.show',
        path: '/admin/players/:id',
        component: () => import('../views/Admin/Players/Show.vue'),
        redirect: to => ({ name: 'admin.players.details', params: to.params as Record<string, unknown> }),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
        },
        params: {} as { id: string },
        children: {
          Details: defineRoute({
            name: 'admin.players.details',
            path: '',
            component: () => import('../views/Admin/Players/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
            },
            params: {} as { id: string },
          }),
          Transactions: defineRoute({
            name: 'admin.players.transactions',
            path: 'transactions',
            component: () => import('../views/Admin/Players/Transactions.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
            },
            params: {} as { id: string },
          }),
          GameSessions: defineRoute({
            name: 'admin.players.game_sessions',
            path: 'game-sessions',
            component: () => import('../views/Admin/Players/GameSessions.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
            },
            params: {} as { id: string },
          }),
        },
      }),
    },

    Employees: {
      Users: {
        Index: defineRoute({
          name: 'admin.employees.users.index',
          path: '/admin/employees/users',
          component: () => import('../views/Admin/Employees/Users/Index.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_SHOW,
            context: 'employee',
          },
          menus: {
            main: { label: 'menu.users', icon: 'pi pi-users', group: menuGroups.main.employees },
          },
        }),
        Create: defineRoute({
          name: 'admin.employees.users.create',
          path: '/admin/employees/users/create',
          component: () => import('../views/Admin/Employees/Users/Create.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_CREATE,
            context: 'employee',
          },
        }),
        Show: defineRoute({
          name: 'admin.employees.users.show',
          path: '/admin/employees/users/:id',
          component: () => import('../views/Admin/Employees/Users/Show.vue'),
          redirect: to => ({ name: 'admin.employees.users.details', params: to.params as Record<string, unknown> }),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_SHOW,
            context: 'employee',
          },
          params: {} as { id: string },
          children: {
            Details: defineRoute({
              name: 'admin.employees.users.details',
              path: '',
              component: () => import('../views/Admin/Employees/Users/DetailsTab.vue'),
              meta: {
                requiresAuth: true,
                requiresEmployee: true,
                permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_SHOW,
                context: 'employee',
              },
              params: {} as { id: string },
            }),
            Access: defineRoute({
              name: 'admin.employees.users.access',
              path: 'access',
              component: () => import('../views/Admin/Employees/Users/AccessTab.vue'),
              meta: {
                requiresAuth: true,
                requiresEmployee: true,
                permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_SHOW,
                context: 'employee',
              },
              params: {} as { id: string },
            }),
          },
        }),
        Edit: defineRoute({
          name: 'admin.employees.users.edit',
          path: '/admin/employees/users/:id/edit',
          component: () => import('../views/Admin/Employees/Users/Edit.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_UPDATE,
            context: 'employee',
          },
          params: {} as { id: string },
        }),
      },
      Roles: {
        Index: defineRoute({
          name: 'admin.employees.roles.index',
          path: '/admin/employees/roles',
          component: () => import('../views/Admin/Employees/Roles/Index.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_ROLES_SHOW,
            context: 'employee',
          },
          menus: {
            main: { label: 'menu.roles', icon: 'pi pi-shield', group: menuGroups.main.employees },
          },
        }),
        Create: defineRoute({
          name: 'admin.employees.roles.create',
          path: '/admin/employees/roles/create',
          component: () => import('../views/Admin/Employees/Roles/Create.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_ROLES_CREATE,
            context: 'employee',
          },
        }),
        Edit: defineRoute({
          name: 'admin.employees.roles.edit',
          path: '/admin/employees/roles/:id/edit',
          component: () => import('../views/Admin/Employees/Roles/Create.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_ROLES_UPDATE,
            context: 'employee',
          },
          params: {} as { id: string },
        }),
        Show: defineRoute({
          name: 'admin.employees.roles.show',
          path: '/admin/employees/roles/:id',
          redirect: to => ({ name: 'admin.employees.roles.details', params: to.params as Record<string, unknown> }),
          component: () => import('../views/Admin/Employees/Roles/Show.vue'),
          meta: {
            requiresAuth: true,
            requiresEmployee: true,
            permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_ROLES_SHOW,
            context: 'employee',
          },
          params: {} as { id: string },
          children: {
            Details: defineRoute({
              path: '',
              name: 'admin.employees.roles.details',
              component: () => import('../views/Admin/Employees/Roles/Details.vue'),
              meta: {
                requiresAuth: true,
                requiresEmployee: true,
                permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_ROLES_SHOW,
                context: 'employee',
              },
              params: {} as { id: string },
            }),
            Employees: defineRoute({
              path: 'employees',
              name: 'admin.employees.roles.employees',
              component: () => import('../views/Admin/Employees/Roles/Employees.vue'),
              meta: {
                requiresAuth: true,
                requiresEmployee: true,
                permission: EMPLOYEE_PERMISSIONS.EMPLOYEES_ROLES_SHOW,
                context: 'employee',
              },
              params: {} as { id: string },
            }),
          },
        }),
      },
    },

    Customers: {
      Index: defineRoute({
        name: 'admin.customers.index',
        path: '/admin/customers',
        component: () => import('../views/Admin/Customers/Index.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.CUSTOMERS_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.customers', icon: 'pi pi-user' },
        },
      }),
      Create: defineRoute({
        name: 'admin.customers.create',
        path: '/admin/customers/create',
        component: () => import('../views/Admin/Customers/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.CUSTOMERS_CREATE,
          context: 'employee',
        },
      }),
      Show: defineRoute({
        name: 'admin.customers.show',
        path: '/admin/customers/:id',
        component: () => import('../views/Admin/Customers/Show.vue'),
        redirect: to => ({ name: 'admin.customers.details', params: to.params as Record<string, unknown> }),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.CUSTOMERS_SHOW,
          context: 'employee',
        },
        params: {} as { id: string },
        children: {
          Details: defineRoute({
            name: 'admin.customers.details',
            path: '',
            component: () => import('../views/Admin/Customers/DetailsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              permission: EMPLOYEE_PERMISSIONS.CUSTOMERS_SHOW,
              context: 'employee',
            },
            params: {} as { id: string },
          }),
          Companies: defineRoute({
            name: 'admin.customers.companies',
            path: 'companies',
            component: () => import('../views/Admin/Customers/CompaniesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              permission: EMPLOYEE_PERMISSIONS.CUSTOMERS_SHOW,
              context: 'employee',
            },
            params: {} as { id: string },
          }),
        },
      }),
      Edit: defineRoute({
        name: 'admin.customers.edit',
        path: '/admin/customers/:id/edit',
        component: () => import('../views/Admin/Customers/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.CUSTOMERS_UPDATE,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
    },

    MainProviders: {
      Index: defineRoute({
        name: 'admin.main-providers.index',
        path: '/admin/main-providers',
        component: () => import('../views/Admin/MainProviders.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.MAIN_PROVIDERS_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.main_providers', icon: 'pi pi-server', group: menuGroups.main.integrations },
        },
      }),
      Show: defineRoute({
        name: 'admin.main-providers.show',
        path: '/admin/main-providers/:id',
        component: () => import('../views/Admin/MainProviders/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.MAIN_PROVIDERS_SHOW,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
      Edit: defineRoute({
        name: 'admin.main-providers.edit',
        path: '/admin/main-providers/:id/edit',
        component: () => import('../views/Admin/MainProviders/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.MAIN_PROVIDERS_UPDATE,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
    },

    Providers: {
      Index: defineRoute({
        name: 'admin.providers.index',
        path: '/admin/providers',
        component: () => import('../views/Admin/Providers.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.PROVIDERS_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.providers', icon: 'pi pi-box', group: menuGroups.main.integrations },
        },
      }),
      Show: defineRoute({
        name: 'admin.providers.show',
        path: '/admin/providers/:id',
        component: () => import('../views/Admin/Providers/Show.vue'),
        redirect: to => ({ name: 'admin.providers.details', params: to.params as Record<string, unknown> }),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.PROVIDERS_SHOW,
          context: 'employee',
        },
        params: {} as { id: string },
        children: {
          Details: defineRoute({
            name: 'admin.providers.details',
            path: '',
            component: () => import('../views/Admin/Providers/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.PROVIDERS_SHOW,
            },
            params: {} as { id: string },
          }),
          Languages: defineRoute({
            name: 'admin.providers.languages',
            path: 'languages',
            component: () => import('../views/Admin/Providers/LanguagesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.PROVIDERS_SHOW,
            },
            params: {} as { id: string },
          }),
          Currencies: defineRoute({
            name: 'admin.providers.currencies',
            path: 'currencies',
            component: () => import('../views/Admin/Providers/CurrenciesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.PROVIDERS_SHOW,
            },
            params: {} as { id: string },
          }),
          Countries: defineRoute({
            name: 'admin.providers.countries',
            path: 'countries',
            component: () => import('../views/Admin/Providers/CountriesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.PROVIDERS_SHOW,
            },
            params: {} as { id: string },
          }),
          Brands: defineRoute({
            name: 'admin.providers.brands',
            path: 'brands',
            component: () => import('../views/Admin/Providers/BrandsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresEmployee: true,
              context: 'employee',
              permission: EMPLOYEE_PERMISSIONS.BRANDS_SHOW,
            },
            params: {} as { id: string },
          }),
        },
      }),
      Edit: defineRoute({
        name: 'admin.providers.edit',
        path: '/admin/providers/:id/edit',
        component: () => import('../views/Admin/Providers/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.PROVIDERS_UPDATE,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
    },

    Games: {
      Index: defineRoute({
        name: 'admin.games.index',
        path: '/admin/games',
        component: () => import('../views/Admin/Games.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.GAMES_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.games', icon: 'pi pi-play', group: menuGroups.main.integrations },
        },
      }),
      Create: defineRoute({
        name: 'admin.games.create',
        path: '/admin/games/create',
        component: () => import('../views/Admin/Games/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.GAMES_CREATE,
          context: 'employee',
        },
      }),
      Show: defineRoute({
        name: 'admin.games.show',
        path: '/admin/games/:id',
        component: () => import('../views/Admin/Games/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.GAMES_SHOW,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
      Edit: defineRoute({
        name: 'admin.games.edit',
        path: '/admin/games/:id/edit',
        component: () => import('../views/Admin/Games/Edit.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.GAMES_UPDATE,
          context: 'employee',
        },
        params: {} as { id: string },
      }),
    },

    LauncherDomains: defineRoute({
      name: 'admin.launcher_domains.index',
      path: '/admin/launcher-domains',
      component: () => import('../views/Admin/LauncherDomains.vue'),
      meta: {
        requiresAuth: true,
        requiresEmployee: true,
        permission: EMPLOYEE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE,
        context: 'employee',
      },
      menus: {
        main: { label: 'menu.launcher_domains', icon: 'pi pi-globe', group: menuGroups.main.integrations },
      },
    }),

    Translations: defineRoute({
      name: 'admin.translations',
      path: '/admin/translations',
      component: () => import('../views/Admin/TranslationManagement.vue'),
      meta: {
        requiresAuth: true,
        requiresEmployee: true,
        permission: EMPLOYEE_PERMISSIONS.TRANSLATIONS_MANAGE,
        context: 'employee',
      },
      menus: {
        main: { label: 'menu.translations', icon: 'pi pi-language' },
      },
    }),

    Reports: {
      GamblingSummary: defineRoute({
        name: 'admin.reports.gambling-summary',
        path: '/admin/reports/gambling-summary',
        component: () => import('../views/Admin/Reports/GamblingSummary.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.REPORTS_GAMBLING_SUMMARY_SHOW,
        },
        menus: {
          main: { label: 'menu.gambling_summary', icon: 'pi pi-chart-line', group: menuGroups.main.reports },
        },
      }),
      PlayersReport: defineRoute({
        name: 'admin.reports.players',
        path: '/admin/reports/players',
        component: () => import('../views/Admin/Reports/PlayersReport.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.REPORTS_PLAYERS_SHOW,
        },
        menus: {
          main: { label: 'menu.players_report', icon: 'pi pi-users', group: menuGroups.main.reports },
        },
      }),
      PromotionsReport: defineRoute({
        name: 'admin.reports.promotions',
        path: '/admin/reports/promotions',
        component: () => import('../views/Admin/Reports/PromotionsReport.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.REPORTS_PROMOTIONS_SHOW,
        },
        menus: {
          main: { label: 'menu.promotions_report', icon: 'pi pi-gift', group: menuGroups.main.reports },
        },
      }),
      GamesReport: defineRoute({
        name: 'admin.reports.games',
        path: '/admin/reports/games',
        component: () => import('../views/Admin/Reports/GamesReport.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.REPORTS_GAMES_SHOW,
        },
        menus: {
          main: { label: 'menu.games_report', icon: 'pi pi-trophy', group: menuGroups.main.reports },
        },
      }),
      GameDetail: defineRoute({
        name: 'admin.reports.games.detail',
        path: '/admin/reports/games/:gameId',
        component: () => import('../views/Admin/Reports/GameDetail.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.REPORTS_GAMES_SHOW,
        },
        params: {} as { gameId: string },
      }),
    },

    ExchangeRates: {
      Index: defineRoute({
        name: 'admin.exchange-rates.index',
        path: '/admin/exchange-rates',
        component: () => import('../views/Admin/ExchangeRates.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.EXCHANGE_RATES_SHOW,
        },
        menus: {
          main: { label: 'menu.exchange_rates', icon: 'pi pi-dollar', group: menuGroups.main.reports },
        },
      }),
    },

    Invoices: {
      Index: defineRoute({
        name: 'admin.invoices.index',
        path: '/admin/invoices',
        component: () => import('../views/Admin/Invoices/Index.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.INVOICES_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.invoice_list', icon: 'pi pi-file-edit', group: menuGroups.main.invoices },
        },
      }),
      DiscountPrograms: defineRoute({
        name: 'admin.invoices.discount_programs',
        path: '/admin/invoices/discount-programs',
        component: () => import('../views/Admin/DiscountPrograms/Index.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.DISCOUNT_PROGRAMS_SHOW,
          context: 'employee',
        },
        menus: {
          main: {
            label: 'menu.discount_programs',
            icon: 'pi pi-ticket',
            group: menuGroups.main.invoices,
          },
        },
      }),
      Aging: defineRoute({
        name: 'admin.invoices.aging',
        path: '/admin/invoices/aging',
        component: () => import('../views/Admin/Invoices/Aging.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.INVOICES_AGING_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.invoice_aging', icon: 'pi pi-clock', group: menuGroups.main.invoices },
        },
      }),
      Commissions: defineRoute({
        name: 'admin.invoices.commissions',
        path: '/admin/invoices/commissions',
        component: () => import('../views/Admin/Invoices/Commissions.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.INVOICES_COMMISSIONS_SHOW,
          context: 'employee',
        },
        menus: {
          main: { label: 'menu.invoice_commissions', icon: 'pi pi-percentage', group: menuGroups.main.invoices },
        },
      }),
      Show: defineRoute({
        name: 'admin.invoices.show',
        path: '/admin/invoices/:brandId',
        component: () => import('../views/Admin/Invoices/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          permission: EMPLOYEE_PERMISSIONS.INVOICES_SHOW,
          context: 'employee',
        },
        params: {} as { brandId: string },
      }),
    },

    ActivityLogs: {
      Index: defineRoute({
        name: 'admin.activity-logs.index',
        path: '/admin/activity-logs',
        component: () => import('../views/Admin/ActivityLogs.vue'),
        meta: {
          requiresAuth: true,
          requiresEmployee: true,
          context: 'employee',
          permission: EMPLOYEE_PERMISSIONS.ACTIVITY_LOGS_SHOW,
        },
        menus: {
          main: { label: 'menu.activity_logs', icon: 'pi pi-history' },
        },
      }),
    },

    Activity: defineRoute({
      name: 'admin.activity',
      path: '/admin/activity',
      component: () => import('../views/ActivityLogs.vue'),
      meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
      menus: {
        user: { label: 'menu.activity_logs', icon: 'pi pi-history' },
      },
    }),

    UserSettings: defineRoute({
      name: 'admin.settings',
      path: '/admin/settings',
      component: () => import('../views/UserSettings.vue'),
      meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
      menus: {
        user: { label: 'menu.user_settings', icon: 'pi pi-cog', order: 5 },
      },
    }),

    Security: {
      TwoFactor: defineRoute({
        name: 'admin.security.2fa',
        path: '/admin/security/2fa',
        component: () => import('../views/Security/TwoFactorAuth.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        menus: {
          user: { label: 'menu.two_factor_auth', icon: 'pi pi-lock' },
        },
      }),
      Password: defineRoute({
        name: 'admin.security.password',
        path: '/admin/security/password',
        component: () => import('../views/Security/ChangePassword.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        menus: {
          user: { label: 'menu.change_password', icon: 'pi pi-key' },
        },
      }),
      Email: defineRoute({
        name: 'admin.security.email',
        path: '/admin/security/email',
        component: () => import('../views/Security/ChangeEmail.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        menus: {
          user: { label: 'menu.change_email', icon: 'pi pi-envelope' },
        },
      }),
      LoginLogs: defineRoute({
        name: 'admin.security.login-logs',
        path: '/admin/security/login-logs',
        component: () => import('../views/LoginLogs.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        menus: {
          user: { label: 'menu.login_history', icon: 'pi pi-history' },
        },
      }),
      Sessions: defineRoute({
        name: 'admin.security.sessions',
        path: '/admin/security/sessions',
        component: () => import('../views/ActiveSessions.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        menus: {
          user: { label: 'menu.active_sessions', icon: 'pi pi-desktop' },
        },
      }),
    },

    Docs: {
      CallbackApi: defineRoute({
        name: 'admin.docs.callback-api',
        path: '/admin/docs/callback-api/:version?/:tab?',
        component: () => import('../views/CallbackApiDocumentation.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        params: {} as { version?: string; tab?: string },
      }),
      PublicApi: defineRoute({
        name: 'admin.docs.public-api',
        path: '/admin/docs/public-api/:version?/:tab?',
        component: () => import('../views/PublicApiDocumentation.vue'),
        meta: { requiresAuth: true, requiresEmployee: true, context: 'employee' },
        params: {} as { version?: string; tab?: string },
      }),
    },
  },

  Customer: {
    Login: defineRoute({
      name: 'customer.login',
      path: '/login',
      component: () => import('../views/Customer/Login.vue'),
      meta: { requiresGuest: true, context: 'customer' },
    }),

    PendingInvitations: defineRoute({
      name: 'customer.pending-invitations',
      path: '/pending-invitations',
      component: () => import('../views/Customer/PendingInvitations.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
    }),

    Dashboard: defineRoute({
      name: 'customer.dashboard',
      path: '/company/:companyId/dashboard',
      component: () => import('../views/Customer/Dashboard.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, requiresCompany: true, context: 'customer' },
      params: {} as { companyId: string },
      menus: {
        main: { label: 'menu.dashboard', icon: 'pi pi-home' },
      },
    }),

    ActivityLogs: defineRoute({
      name: 'customer.activity_logs',
      path: '/company/:companyId/activity-logs',
      component: () => import('../views/Customer/ActivityLogs.vue'),
      meta: {
        requiresAuth: true,
        requiresCustomer: true,
        requiresCompany: true,
        permission: CUSTOMER_PERMISSIONS.COMPANY_ACTIVITY_LOG_VIEW,
        context: 'customer',
      },
      params: {} as { companyId: string },
      menus: {
        main: { label: 'menu.activity_logs', icon: 'pi pi-history' },
      },
    }),

    Roles: {
      Index: defineRoute({
        name: 'customer.roles.index',
        path: '/company/:companyId/roles',
        component: () => import('../views/Customer/Roles/Index.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.ROLES_INDEX,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.roles', icon: 'pi pi-shield' },
        },
      }),
      Create: defineRoute({
        name: 'customer.roles.create',
        path: '/company/:companyId/roles/create',
        component: () => import('../views/Customer/Roles/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.ROLES_CREATE,
          context: 'customer',
        },
        params: {} as { companyId: string },
      }),
      Edit: defineRoute({
        name: 'customer.roles.edit',
        path: '/company/:companyId/roles/:id/edit',
        component: () => import('../views/Customer/Roles/Create.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.ROLES_UPDATE,
          context: 'customer',
        },
        params: {} as { companyId: string; id: string },
      }),
      Show: defineRoute({
        name: 'customer.roles.show',
        path: '/company/:companyId/roles/:id',
        redirect: to => ({ name: 'customer.roles.details', params: to.params as Record<string, unknown> }),
        component: () => import('../views/Customer/Roles/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.ROLES_INDEX,
          context: 'customer',
        },
        params: {} as { companyId: string; id: string },
        children: {
          Details: defineRoute({
            name: 'customer.roles.details',
            path: '',
            component: () => import('../views/Customer/Roles/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.ROLES_INDEX,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          Users: defineRoute({
            name: 'customer.roles.users',
            path: 'users',
            component: () => import('../views/Customer/Roles/Users.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.ROLES_INDEX,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
        },
      }),
    },

    Users: {
      Index: defineRoute({
        name: 'customer.users.index',
        path: '/company/:companyId/users',
        component: () => import('../views/Customer/Users/Index.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.USERS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.users', icon: 'pi pi-users' },
        },
      }),
      Show: defineRoute({
        name: 'customer.users.show',
        path: '/company/:companyId/users/:id',
        component: () => import('../views/Customer/Users/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.USERS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string; id: string },
      }),
    },

    Brands: {
      Index: defineRoute({
        name: 'customer.brands.index',
        path: '/company/:companyId/brands',
        component: () => import('../views/Customer/Brands/Index.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.BRANDS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.brands', icon: 'pi pi-tag' },
        },
      }),
      Show: defineRoute({
        name: 'customer.brands.show',
        path: '/company/:companyId/brands/:id',
        component: () => import('../views/Customer/Brands/Show.vue'),
        redirect: to => ({ name: 'customer.brands.details', params: to.params as Record<string, unknown> }),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.BRANDS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string; id: string },
        children: {
          Details: defineRoute({
            name: 'customer.brands.details',
            path: '',
            component: () => import('../views/Customer/Brands/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          Markets: defineRoute({
            name: 'customer.brands.markets',
            path: 'markets',
            component: () => import('../views/Customer/Brands/MarketsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_MARKETS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          Credentials: defineRoute({
            name: 'customer.brands.credentials',
            path: 'credentials',
            component: () => import('../views/Customer/Brands/CredentialsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          CallbackUrls: defineRoute({
            name: 'customer.brands.callback-urls',
            path: 'callback-urls',
            component: () => import('../views/Customer/Brands/CallbackUrlsTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_CALLBACK_URLS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          Providers: defineRoute({
            name: 'customer.brands.providers',
            path: 'providers',
            component: () => import('../views/Customer/Brands/ProvidersTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_PROVIDERS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          Games: defineRoute({
            name: 'customer.brands.games',
            path: 'games',
            component: () => import('../views/Customer/Brands/GamesTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_GAMES_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          CallbackTests: defineRoute({
            name: 'customer.brands.callback_tests',
            path: 'callback-tests',
            component: () => import('../views/Customer/Brands/CallbackTests.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_CALLBACK_TESTS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          RequestAudit: defineRoute({
            name: 'customer.brands.request-audit',
            path: 'request-audit',
            component: () => import('../views/Customer/Brands/CallbackAuditTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_CALLBACK_URLS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          PublicApi: defineRoute({
            name: 'customer.brands.public_api',
            path: 'public-api',
            component: () => import('../views/Customer/Brands/PublicApi.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_PUBLIC_API_TESTING_USE,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          ActivityLog: defineRoute({
            name: 'customer.brands.activity_log',
            path: 'activity-log',
            component: () => import('../views/Customer/Brands/ActivityLogTab.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.BRANDS_ACTIVITY_LOG_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
        },
      }),
    },

    Players: {
      Index: defineRoute({
        name: 'customer.players.index',
        path: '/company/:companyId/players',
        component: () => import('../views/Customer/Players.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.PLAYERS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.players', icon: 'pi pi-users' },
        },
      }),
      Show: defineRoute({
        name: 'customer.players.show',
        path: '/company/:companyId/players/:id',
        component: () => import('../views/Customer/Players/Show.vue'),
        redirect: to => ({ name: 'customer.players.details', params: to.params as Record<string, unknown> }),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.PLAYERS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string; id: string },
        children: {
          Details: defineRoute({
            name: 'customer.players.details',
            path: '',
            component: () => import('../views/Customer/Players/Details.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.PLAYERS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          Transactions: defineRoute({
            name: 'customer.players.transactions',
            path: 'transactions',
            component: () => import('../views/Customer/Players/Transactions.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.PLAYERS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
          GameSessions: defineRoute({
            name: 'customer.players.game_sessions',
            path: 'game-sessions',
            component: () => import('../views/Customer/Players/GameSessions.vue'),
            meta: {
              requiresAuth: true,
              requiresCustomer: true,
              requiresCompany: true,
              permission: CUSTOMER_PERMISSIONS.PLAYERS_VIEW,
              context: 'customer',
            },
            params: {} as { companyId: string; id: string },
          }),
        },
      }),
    },

    Transactions: {
      Dashboard: defineRoute({
        name: 'customer.transactions.dashboard',
        path: '/company/:companyId/transactions',
        component: () => import('../views/Customer/Transactions/Dashboard.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.TRANSACTIONS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.transactions', icon: 'pi pi-chart-bar' },
        },
      }),
      Live: defineRoute({
        name: 'customer.transactions.live',
        path: '/company/:companyId/transactions/live',
        component: () => import('../views/Customer/Transactions/Live.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.TRANSACTIONS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
      }),
      Details: defineRoute({
        name: 'customer.transactions.details',
        path: '/company/:companyId/transactions/:transactionId',
        component: () => import('../views/Customer/Transactions/Details.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.TRANSACTIONS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string; transactionId: string },
      }),
    },

    GameSessions: {
      Show: defineRoute({
        name: 'customer.game_sessions.show',
        path: '/company/:companyId/game-sessions/:id',
        component: () => import('../views/Customer/GameSessions/Show.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.PLAYERS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string; id: string },
      }),
    },

    Settings: defineRoute({
      name: 'customer.settings',
      path: '/company/:companyId/settings',
      component: () => import('../views/Customer/Settings.vue'),
      meta: {
        requiresAuth: true,
        requiresCustomer: true,
        requiresCompany: true,
        context: 'customer',
        permission: CUSTOMER_PERMISSIONS.COMPANY_SETTINGS_VIEW,
      },
      params: {} as { companyId: string },
      menus: {
        main: { label: 'menu.settings', icon: 'pi pi-cog' },
      },
    }),

    Invitations: defineRoute({
      name: 'customer.invitations',
      path: '/company/:companyId/invitations',
      component: () => import('../views/Customer/Invitations.vue'),
      meta: {
        requiresAuth: true,
        requiresCustomer: true,
        requiresCompany: true,
        requiresOwner: true,
        context: 'customer',
      },
      params: {} as { companyId: string },
      menus: {},
    }),

    Reports: {
      GamblingSummary: defineRoute({
        name: 'customer.reports.gambling-summary',
        path: '/company/:companyId/reports/gambling-summary',
        component: () => import('../views/Customer/Reports/GamblingSummary.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.REPORTS_GAMBLING_SUMMARY_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.gambling_summary', icon: 'pi pi-chart-line', group: 'menu.reports' },
        },
      }),
      PlayersReport: defineRoute({
        name: 'customer.reports.players',
        path: '/company/:companyId/reports/players',
        component: () => import('../views/Customer/Reports/PlayersReport.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.REPORTS_PLAYERS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.players_report', icon: 'pi pi-users', group: 'menu.reports' },
        },
      }),
      PromotionsReport: defineRoute({
        name: 'customer.reports.promotions',
        path: '/company/:companyId/reports/promotions',
        component: () => import('../views/Customer/Reports/PromotionsReport.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.REPORTS_PROMOTIONS_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.promotions_report', icon: 'pi pi-gift', group: 'menu.reports' },
        },
      }),
      GamesReport: defineRoute({
        name: 'customer.reports.games',
        path: '/company/:companyId/reports/games',
        component: () => import('../views/Customer/Reports/GamesReport.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.REPORTS_GAMES_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string },
        menus: {
          main: { label: 'menu.games_report', icon: 'pi pi-trophy', group: 'menu.reports' },
        },
      }),
      GameDetail: defineRoute({
        name: 'customer.reports.games.detail',
        path: '/company/:companyId/reports/games/:gameId',
        component: () => import('../views/Customer/Reports/GameDetail.vue'),
        meta: {
          requiresAuth: true,
          requiresCustomer: true,
          requiresCompany: true,
          permission: CUSTOMER_PERMISSIONS.REPORTS_GAMES_VIEW,
          context: 'customer',
        },
        params: {} as { companyId: string; gameId: string },
      }),
    },

    Docs: {
      CallbackApi: defineRoute({
        name: 'customer.docs.callback-api',
        path: '/company/:companyId/docs/callback-api/:version?/:tab?',
        component: () => import('../views/CallbackApiDocumentation.vue'),
        meta: { requiresAuth: true, requiresCustomer: true, requiresCompany: true, context: 'customer' },
        params: {} as { companyId: string; version?: string; tab?: string },
      }),
      PublicApi: defineRoute({
        name: 'customer.docs.public-api',
        path: '/company/:companyId/docs/public-api/:version?/:tab?',
        component: () => import('../views/PublicApiDocumentation.vue'),
        meta: { requiresAuth: true, requiresCustomer: true, requiresCompany: true, context: 'customer' },
        params: {} as { companyId: string; version?: string; tab?: string },
      }),
    },
  },

  UserSettings: defineRoute({
    name: 'customer.user.settings',
    path: '/user/settings',
    component: () => import('../views/UserSettings.vue'),
    meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
    menus: {
      user: { label: 'menu.user_settings', icon: 'pi pi-cog', order: 5 },
    },
  }),

  Security: {
    Activity: defineRoute({
      name: 'security.activity',
      path: '/security/activity',
      component: () => import('../views/ActivityLogs.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
      menus: {
        user: { label: 'menu.activity_logs', icon: 'pi pi-history' },
      },
    }),
    TwoFactor: defineRoute({
      name: 'security.2fa',
      path: '/security/2fa',
      component: () => import('../views/Security/TwoFactorAuth.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
      menus: {
        user: { label: 'menu.two_factor_auth', icon: 'pi pi-lock' },
      },
    }),
    Password: defineRoute({
      name: 'security.password',
      path: '/security/password',
      component: () => import('../views/Security/ChangePassword.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
      menus: {
        user: { label: 'menu.change_password', icon: 'pi pi-key' },
      },
    }),
    Email: defineRoute({
      name: 'security.email',
      path: '/security/email',
      component: () => import('../views/Security/ChangeEmail.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
      menus: {
        user: { label: 'menu.change_email', icon: 'pi pi-envelope' },
      },
    }),
    LoginLogs: defineRoute({
      name: 'security.login-logs',
      path: '/security/login-logs',
      component: () => import('../views/LoginLogs.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
      menus: {
        user: { label: 'menu.login_history', icon: 'pi pi-history' },
      },
    }),
    Sessions: defineRoute({
      name: 'security.sessions',
      path: '/security/sessions',
      component: () => import('../views/ActiveSessions.vue'),
      meta: { requiresAuth: true, requiresCustomer: true, context: 'customer' },
      menus: {
        user: { label: 'menu.active_sessions', icon: 'pi pi-desktop' },
      },
    }),
  },

  Invitation: {
    Accept: defineRoute({
      name: 'invitation.accept',
      path: '/invitation/:token',
      component: () => import('../views/AcceptInvitation.vue'),
      meta: { requiresGuest: false, requiresAuth: false },
      params: {} as { token: string },
    }),
  },

  RateLimited: defineRoute({
    name: 'rate-limited',
    path: '/rate-limited',
    component: () => import('../views/RateLimited.vue'),
    meta: { hideNavbar: true },
  }),

  NotFound: defineRoute({
    name: 'not-found',
    path: '/:pathMatch(.*)*',
    redirect: '/',
  }),
} as const;
